import React from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { messages } from '../constants/messages';
import { useContent } from '../ContentContext';

const Footer = () => {
  const { content } = useContent();
  const fs = (content as any).footer_settings || messages.footer;
  const common = content.common;

  const bgColor  = fs.bg_color    || '#0A1D2D';
  const accent   = fs.accent_color || '#EF5D22';

  // Property list stored as pipe-separated string or array fallback
  const propList: string[] = typeof fs.propertyList === 'string'
    ? fs.propertyList.split('|').map((s: string) => s.trim()).filter(Boolean)
    : (fs.propertyList || messages.footer.propertyList);

  const navItems = (content as any).navArray || [];

  return (
    <footer style={{ backgroundColor: bgColor }} className="text-white py-12 sm:py-20 px-4 sm:px-10">
      <div className="container mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">

        {/* Brand column */}
        <div className="col-span-1 md:col-span-1">
          <div className="flex items-center space-x-2 mb-8">
            <img
              src={(content as any).logo_url || '/images/mainLogo.png'}
              alt={common.companyName || messages.common.companyName}
              className="h-12 w-auto brightness-0 invert"
            />
          </div>
          <p className="text-gray-400 text-base leading-relaxed mb-8">
            {fs.aboutText || messages.footer.aboutText}
          </p>
          <div className="flex space-x-4">
            {common.social?.facebook && (
              <a href={common.social.facebook} target="_blank" rel="noopener noreferrer"
                style={{ ['--accent' as any]: accent }}
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[var(--accent)] transition-colors cursor-pointer">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
            )}
            {common.social?.instagram && (
              <a href={common.social.instagram} target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:opacity-80 transition-colors cursor-pointer"
                style={{ ['--accent' as any]: accent }}>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.805.249 2.227.412.56.216.96.474 1.38.894.42.42.678.82.894 1.38.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.054 1.17-.249 1.805-.412 2.227-.216.56-.474.96-.894 1.38-.42.42-.82.678-1.38.894-.422.164-1.057.36-2.227.413-1.266.057-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.054-1.805-.249-2.227-.412-.56-.216-.96-.474-1.38-.894-.42-.42-.678-.82-.894-1.38-.164-.422-.36-1.057-.413-2.227-.057-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.054-1.17.249-1.805.412-2.227.216-.56.474-.96.894-1.38.42-.42.82-.678 1.38-.894.422-.164 1.057-.36 2.227-.413 1.266-.057 1.646-.07 4.85-.07zM12 0C8.741 0 8.333.014 7.053.072 5.775.131 4.903.335 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.335 4.903.131 5.775.072 7.053.014 8.333 0 8.741 0 12s.014 3.667.072 4.947c.059 1.277.263 2.148.558 2.911.306.788.717 1.459 1.384 2.126s1.337 1.079 2.126 1.384c.763.295 1.634.499 2.911.558 1.28.059 1.689.072 4.947.072s3.667-.014 4.947-.072c1.277-.059 2.148-.263 2.911-.558.788-.306 1.459-.717 2.126-1.384s1.079-1.337 1.384-2.126c.295-.763.499-1.634.558-2.911.059-1.28.072-1.689.072-4.947s-.014-3.667-.072-4.947c-.059-1.277-.263-2.148-.558-2.911-.306-.788-.717-1.459-1.384-2.126s-1.337-1.079-2.126-1.384c-.763-.295-1.634-.499-2.911-.558C15.667.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 11-2.88 0 1.44 1.44 0 012.88 0z"/></svg>
              </a>
            )}
            {common.social?.whatsapp && (
              <a href={common.social.whatsapp} target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:opacity-80 transition-colors cursor-pointer">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.438 9.889-9.886.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.894 4.44-9.899 9.888-.001 2.225.586 3.891 1.546 5.45l-.971 3.546 3.673-.963zm13.155-7.144c-.272-.136-1.613-.797-1.863-.888-.249-.091-.43-.136-.611.136-.182.273-.703.888-.862 1.069-.159.182-.317.204-.589.068-.272-.136-1.15-.424-2.191-1.354-.81-.722-1.356-1.615-1.515-1.887-.159-.272-.017-.42.119-.556.123-.122.272-.318.408-.477.136-.159.182-.272.272-.454.091-.182.045-.341-.023-.477-.068-.136-.611-1.477-.839-2.022-.222-.534-.467-.46-.611-.468-.141-.008-.302-.008-.462-.008-.161 0-.422.06-.644.302-.222.242-.848.832-.848 2.029 0 1.197.87 2.354.991 2.52.12.167 1.711 2.613 4.14 3.662.578.249 1.028.397 1.38.509.58.185 1.109.158 1.527.096.466-.07 1.613-.659 1.841-1.295.227-.636.227-1.181.159-1.295-.068-.113-.249-.182-.522-.318z"/></svg>
              </a>
            )}
            {common.social?.telegram && (
              <a href={common.social.telegram} target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:opacity-80 transition-colors cursor-pointer">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.13-.31-1.08-.66.02-.18.27-.36.74-.55 2.91-1.27 4.85-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .33z"/></svg>
              </a>
            )}
          </div>
        </div>

        {/* Quick links */}
        <div>
          <h4 className="font-extrabold text-xl mb-8 border-l-4 pl-4" style={{ borderColor: accent }}>
            {fs.quickLinksTitle || messages.footer.quickLinks}
          </h4>
          <ul className="space-y-4 text-gray-400 text-base">
            {navItems.length > 0
              ? navItems.slice(0, 6).map((item: any) => (
                  <li key={item.id}><Link to={item.link} className="hover:text-white transition-colors" style={{ ['--accent' as any]: accent }}>{item.title}</Link></li>
                ))
              : (
                <>
                  <li><Link to="/home" className="hover:text-white transition-colors">Home</Link></li>
                  <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                  <li><Link to="/highlights" className="hover:text-white transition-colors">Highlights</Link></li>
                  <li><Link to="/amenities" className="hover:text-white transition-colors">Amenities</Link></li>
                  <li><Link to="/gallery" className="hover:text-white transition-colors">Gallery</Link></li>
                </>
              )
            }
          </ul>
        </div>

        {/* Properties */}
        <div>
          <h4 className="font-extrabold text-xl mb-8 border-l-4 pl-4" style={{ borderColor: accent }}>
            {fs.propertiesTitle || messages.footer.properties}
          </h4>
          <ul className="space-y-4 text-gray-400 text-base">
            {propList.map((prop, idx) => (
              <li key={idx} className="hover:text-white transition-colors cursor-pointer">{prop}</li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-extrabold text-xl mb-8 border-l-4 pl-4" style={{ borderColor: accent }}>
            {fs.contactInfoTitle || messages.footer.contactInfo}
          </h4>
          <ul className="space-y-6 text-gray-400 text-base">
            <li className="flex items-start space-x-4">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0" style={{ color: accent }}>
                <MapPinIcon className="w-5 h-5" />
              </div>
              <span className="pt-1">{common.address || messages.common.address}</span>
            </li>
            <li className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0" style={{ color: accent }}>
                <PhoneIcon className="w-5 h-5" />
              </div>
              <span>{common.phone || messages.common.phone}</span>
            </li>
            <li className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0" style={{ color: accent }}>
                <EnvelopeIcon className="w-5 h-5" />
              </div>
              <span>{common.email || messages.common.email}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="container mx-auto mt-12 sm:mt-20 pt-6 sm:pt-8 border-t border-white/10">
        <div className="flex flex-col md:flex-row justify-between items-center text-gray-500 text-xs sm:text-sm space-y-4 md:space-y-0">
          <p className="text-center md:text-left">{fs.copyright || messages.footer.copyright}</p>
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8">
            <Link to="/privacy-policy" className="hover:text-white cursor-pointer transition-colors">{fs.privacy || messages.footer.privacy}</Link>
            <Link to="/terms-of-service" className="hover:text-white cursor-pointer transition-colors">{fs.terms || messages.footer.terms}</Link>
            <Link to="/cookie-policy" className="hover:text-white cursor-pointer transition-colors">Cookie Policy</Link>
          </div>
        </div>
        
        {/* Developed by OETechs */}
        <div className="mt-8 pt-6 border-t border-white/5 flex justify-center items-center">
          <a 
            href="https://www.oetechs.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 text-gray-500 hover:text-white transition-colors group"
          >
            <span className="text-xs uppercase tracking-wider">Developed by</span>
            <img 
              src="/images/oetechs.png" 
              alt="OETechs" 
              className="h-6 w-auto opacity-60 group-hover:opacity-100 transition-opacity"
            />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
